<template>
  <PsModal
    v-model:show="curShow"
    header="История работы над новостью"
    size="lg"
    :bodyPadding="false"
  >
    <PsTable
      :data="tableData"
      :columns="columns"
      :loading="isLoading"
    />
  </PsModal>
</template>

<script setup lang="ts">
import { useAsyncState, useVModel } from '@vueuse/core'
import { computed, inject } from 'vue'
import { noop } from 'lodash-es'
import { dateTimeFormat } from '@/utils/dates'
import PsModal from '@/ContextTab/components/UI/PsModal.vue'
import PsTable from '@/ContextTab/components/UI/Table/PsTable.vue'
import { formatDescription } from '@/utils/formatDescription'
import { formatUsername } from '@/utils/formatUsername'
import { ENewsEventTypeLabel } from '@/lib/references'
import { apiClientV1 } from '@/ContextTab/services/apiClient.ts'
import { NotificationHandlerKey } from '@/injectionKeys.ts'
import type { TableColumn } from '@/ContextTab/components/UI/Table/types'
import type { DataOfNewsEventListGetOut } from '@/api/generated/v1'


interface Props {
  show: boolean
  id: number
}

interface PublishedHistoryData {
  dateTime: string
  userName: string
  action: string
  description: string
}

const props = withDefaults(defineProps<Props>(), {})

const emit = defineEmits<{
  (e: 'update:show', value: (typeof props)['show']): void
}>()

const curShow = useVModel(props, 'show', emit)

const { onError } = inject(NotificationHandlerKey, { onError: noop })

const { state: history, isLoading } = useAsyncState(
  loadHistory(props.id),
  undefined,
  { onError },
)

async function loadHistory(news_id: number): Promise<DataOfNewsEventListGetOut[] | undefined> {
  const response = await apiClientV1.NewsService.getNewsByNewsIdEvents({
    path: {
      news_id,
    },
    query: {
      limit: 100,
    },
  })

  if (response.error) {
    throw new Error('Не удалось загрузить историю работы над новостью')
  }

  return response.data.data
}

const columns: TableColumn<PublishedHistoryData>[] = [
  {
    title: 'Дата и время',
    key: 'dateTime',
    titleAlign: 'center',
    align: 'center',
    render(row) {
      return dateTimeFormat(new Date(row.dateTime), { month: 'short' }).replace(/, /, '\r\n')
    },
    className: '_white-space:preline',
  },
  {
    title: 'Пользователь',
    key: 'userName',
    align: 'center',
    titleAlign: 'center',
    render(row) {
      return formatUsername(row.userName)
    },
  },
  {
    title: 'Действие',
    key: 'action',
    align: 'center',
    titleAlign: 'center',
    render(row) {
      return ENewsEventTypeLabel[row.action] || ''
    },
  },
  {
    title: 'Описание',
    key: 'description',
    align: 'left',
    titleAlign: 'center',
    render(row) {
      return formatDescription(row.description || '')
    },
    className: '_white-space:preline',
  },
]

const tableData = computed<PublishedHistoryData[]>(() => {
  if (!history.value?.length) {
    return []
  }

  return history.value?.map((row: any) => ({
    dateTime: row.created_at,
    userName: row.user?.name,
    action: row.type_id,
    description: row.description,
  }))
})
</script>
